import React, {useEffect, useState} from 'react';
import '../ConfirmWindow/ConfirmationWindow.scss';
import './DeleteAccountModal.scss';
import {AlertTriangle, X} from 'lucide-react';
import {toast} from "react-toastify";
import {useNavigate} from "react-router-dom";
import Button from "../../ui/Button/Button";
import Input from "../../ui/Input/Input";
import {useProfileStore} from "../../../store/profile/pofile.store";
import {useCurrentChatStore} from "../../../store";
import {firebase_sign_out, hasPasswordSignIn, reauthenticate} from "../../../services/firebase_services";
import {auth_delete_account} from "../../../api/auth_api";

const CONFIRM_WORD = 'DELETE';

const reauthErrorMessage = (err: any) => {
    if (['auth/wrong-password', 'auth/invalid-credential'].includes(err?.code)) return 'Wrong password.';
    if (err?.code === 'auth/too-many-requests') return 'Too many attempts. Please wait a moment and try again.';
    if (err?.message === 'NOT_SIGNED_IN') return 'Please sign out, sign in again and retry.';
    return 'Could not confirm your identity. Please try again.';
};

const DeleteAccountModal = ({closeMenu}: { closeMenu: () => void }) => {
    const {user, clearUser}: any = useProfileStore();
    const {resetChat} = useCurrentChatStore();
    const navigate = useNavigate();
    const [confirmText, setConfirmText] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [deleting, setDeleting] = useState(false);
    const usesPassword = hasPasswordSignIn();
    const chatCount = user?.chats?.length || 0;

    useEffect(() => {
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && !deleting) closeMenu();
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [closeMenu, deleting]);

    const canDelete = confirmText.trim() === CONFIRM_WORD && (!usesPassword || password.length > 0) && !deleting;

    const deleteAccount = async () => {
        setError('');
        setDeleting(true);
        try {
            // 1. Confirm it's really them (password or Google) before anything is deleted
            try {
                await reauthenticate(password);
            } catch (err: any) {
                if (err?.code !== 'auth/popup-closed-by-user' && err?.code !== 'auth/cancelled-popup-request') {
                    setError(reauthErrorMessage(err));
                }
                return;
            }
            // 2. The backend deletes the subscription, chats, profile and the Firebase login itself
            await auth_delete_account();
            await firebase_sign_out();
            toast.success('Your account has been deleted.');

            resetChat();
            clearUser();
            navigate('/auth');
        } catch (err) {
            setError('Something went wrong while deleting your account. Please try again or contact support.');
        } finally {
            setDeleting(false);
        }
    };

    return (
        <div className="ConfirmWindow" role="dialog" aria-modal="true" aria-labelledby="deleteAccountTitle">
            <div className="ConfirmWindow__blur" onClick={() => !deleting && closeMenu()}></div>

            <div className="ConfirmWindow__form DeleteAccount">
                <button type="button" className="icon-button DeleteAccount__close" aria-label="Close"
                        onClick={closeMenu} disabled={deleting}>
                    <X size={18}/>
                </button>

                <div className="icon-tile DeleteAccount__icon"><AlertTriangle size={22}/></div>
                <h2 id="deleteAccountTitle">Delete your account?</h2>
                <p>This permanently deletes, and can't be undone:</p>
                <ul className="DeleteAccount__list">
                    <li>Your profile and sign-in for <b>{user?.email}</b></li>
                    <li>{chatCount} AI {chatCount === 1 ? 'chat' : 'chats'} with all messages, photos and cloned voices
                    </li>
                    {user?.plan?.paid && <li>Your {user.plan.name} subscription, cancelled right away</li>}
                </ul>

                {
                    usesPassword && <div className="DeleteAccount__field">
                        <span>Enter your password to confirm</span>
                        <Input type={'password'} title={'Password'} autoComplete={'current-password'} value={password}
                               onChange={(e) => setPassword(e.target.value)}/>
                    </div>
                }

                <div className="DeleteAccount__field">
                    <span>Type <b>{CONFIRM_WORD}</b> to confirm</span>
                    <Input type={'text'} title={CONFIRM_WORD} value={confirmText} autoComplete={'off'}
                           onChange={(e) => setConfirmText(e.target.value)}/>
                </div>

                {!usesPassword && <span className="DeleteAccount__hint">You'll be asked to sign in with Google once more.</span>}
                {error && <span className="DeleteAccount__error" role="alert">{error}</span>}

                <div className="row buttonsRow">
                    <Button type={'button'} onClick={closeMenu} variant={'secondary'} title={'Cancel'}
                            disabled={deleting}/>
                    <Button type={'button'} onClick={deleteAccount} variant={'danger'} disabled={!canDelete}
                            title={deleting ? 'Deleting...' : 'Delete account'}/>
                </div>
            </div>
        </div>
    );
};

export default DeleteAccountModal;
